import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [resetToken, setResetToken] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
            setSubmitted(true);
            
            // If token is returned (dev mode without email), show it
            if (response.data.token) {
                setResetToken(response.data.token);
            }
            
            toast.success('If an account exists with that email, a reset link has been sent.');
        } catch (error) {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col gradient-bg">
            {/* Navigation Bar */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                <span className="font-bold text-lg text-white">Autoimmune Pathways</span>
                                <span className="text-xs sm:text-sm text-slate-400">by Me and My Pandas</span>
                            </div>
                        </Link>
                        <Link to="/login">
                            <Button variant="ghost" className="text-slate-300 hover:text-white">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Login
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center p-6 pt-24">
                <Card className="w-full max-w-md border-0 shadow-xl bg-card/80 backdrop-blur-xl">
                    <CardHeader className="text-center space-y-2">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/30">
                            {submitted ? (
                                <CheckCircle className="w-7 h-7 text-white" />
                            ) : (
                                <Mail className="w-7 h-7 text-white" />
                            )}
                        </div>
                        <CardTitle className="font-heading text-2xl">
                            {submitted ? 'Check Your Email' : 'Forgot Password?'}
                        </CardTitle>
                        <CardDescription>
                            {submitted 
                                ? "We've sent you instructions to reset your password."
                                : "Enter your email and we'll send you a reset link."
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {!submitted ? (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email Address</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="h-11"
                                        data-testid="forgot-email"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full h-11 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-medium shadow-lg shadow-primary/25"
                                    disabled={loading}
                                    data-testid="forgot-submit"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        'Send Reset Link'
                                    )}
                                </Button>
                            </form>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-sm text-muted-foreground text-center">
                                    Didn't receive the email? Check your spam folder or try again.
                                </p>
                                
                                {/* Show reset link in dev mode */}
                                {resetToken && (
                                    <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                        <p className="text-xs text-amber-400 mb-2 font-medium">Development Mode - Reset Link:</p>
                                        <Link 
                                            to={`/reset-password?token=${resetToken}`}
                                            className="text-sm text-primary hover:underline break-all"
                                        >
                                            Click here to reset your password
                                        </Link>
                                    </div>
                                )}
                                
                                <Button
                                    onClick={() => {
                                        setSubmitted(false);
                                        setResetToken(null);
                                    }}
                                    variant="outline"
                                    className="w-full"
                                >
                                    Try Again
                                </Button>
                            </div>
                        )}

                        <div className="mt-6 text-center">
                            <p className="text-sm text-muted-foreground">
                                Remember your password?{' '}
                                <Link
                                    to="/login"
                                    className="font-medium text-primary hover:underline"
                                >
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
